import React, { useState } from 'react'
import Modal from './Modal'
import { FiTrash2, FiAlertTriangle } from 'react-icons/fi'

interface DeleteAccountDialogProps {
    isOpen: boolean
    onClose: () => void
    onConfirm: () => void
    loading?: boolean
}

const DeleteAccountDialog: React.FC<DeleteAccountDialogProps> = ({
    isOpen,
    onClose,
    onConfirm,
    loading = false
}) => {
    const [confirmText, setConfirmText] = useState('')

    const handleConfirm = () => {
        if (confirmText === 'DELETE') {
            onConfirm()
            // Reset on close should be handled by parent or after operation
        }
    }

    const handleClose = () => {
        setConfirmText('')
        onClose()
    }

    const isConfirmed = confirmText === 'DELETE'

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="sm">
            <div className="p-6">
                <div className="sm:flex sm:items-start">
                    {/* Icon */}
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20 sm:mx-0 sm:h-10 sm:w-10">
                        <div className="text-red-600 dark:text-red-400">
                            <FiTrash2 className="w-6 h-6" />
                        </div>
                    </div>

                    {/* Content */}
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                            Delete Account Permanently
                        </h3>
                        <div className="mt-2">
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                                This action <strong>cannot be undone</strong>. Your account and all associated data (profile, gigs, messages, reviews) will be permanently deleted.
                            </p>

                            {/* Warning Box */}
                            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-3 mb-4">
                                <div className="flex gap-2">
                                    <FiAlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                                    <p className="text-sm text-red-800 dark:text-red-300">
                                        You will <strong>not</strong> be able to recover your account after deletion.
                                    </p>
                                </div>
                            </div>

                            {/* Confirmation Input */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    To confirm, type <span className="font-bold text-red-600 dark:text-red-400">DELETE</span>
                                </label>
                                <input
                                    type="text"
                                    value={confirmText}
                                    onChange={(e) => setConfirmText(e.target.value)}
                                    placeholder="Type DELETE"
                                    disabled={loading}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                                        bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                                        placeholder-gray-400 dark:placeholder-gray-500
                                        focus:outline-none focus:ring-2 focus:ring-red-500
                                        disabled:opacity-50 disabled:cursor-not-allowed
                                        transition-colors duration-200"
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter' && isConfirmed && !loading) {
                                            handleConfirm()
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="mt-6 sm:flex sm:flex-row-reverse gap-3">
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={!isConfirmed || loading}
                        className={`
                            w-full justify-center rounded-lg border border-transparent px-4 py-2 text-base font-medium
                            ${isConfirmed && !loading
                                ? 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 cursor-pointer'
                                : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                            }
                            shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2
                            sm:w-auto sm:text-sm
                            transition-colors duration-200
                        `}
                    >
                        {loading ? (
                            <div className="flex items-center justify-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                <span className="ml-2">Deleting...</span>
                            </div>
                        ) : (
                            'Permanently Delete'
                        )}
                    </button>

                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={loading}
                        className="
                            mt-3 w-full justify-center rounded-lg border border-gray-300 dark:border-gray-600
                            bg-white dark:bg-gray-700 px-4 py-2 text-base font-medium
                            text-gray-700 dark:text-gray-300
                            shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600
                            focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                            sm:mt-0 sm:w-auto sm:text-sm
                            disabled:opacity-50 disabled:cursor-not-allowed
                            transition-colors duration-200
                        "
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </Modal>
    )
}

export default DeleteAccountDialog